import React, { useState, useEffect, useCallback } from 'react'
import { ArrowLeft, DollarSign, BarChart3, TrendingUp, Users, Heart, MessageCircle, Share2, Eye, Loader2 } from 'lucide-react'
import { KPICard } from '../ui'
import { SystemTenantCard } from '../../services/systemDashboardService'
import { Cell } from '../content-performance/mockData'

const fmtEMV = (n: number): string =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(2)}K` : `$${(n || 0).toFixed(2)}`
const fmtNum = (n: number): string =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : `${Math.round(n || 0)}`

interface Props {
  categoryLabel: string
  format: string
  cell: Cell
  fetchTenants: () => Promise<SystemTenantCard[]>
  onTenantClick: (customerId: string, customerName: string) => void
  onBack: () => void
}

/**
 * Cross-tenant drill-down (support): the tenants that have content in a clicked category × format
 * cell, as cards ranked by EMV. Click a tenant → that tenant's posts. Followers reflect only the
 * accounts that posted in this category.
 */
const TenantCategoryView: React.FC<Props> = ({ categoryLabel, format, cell, fetchTenants, onTenantClick, onBack }) => {
  const [tenants, setTenants] = useState<SystemTenantCard[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try { setTenants(await fetchTenants()) } catch { setError('Failed to load tenants'); setTenants([]) } finally { setLoading(false) }
  }, [fetchTenants])
  useEffect(() => { load() }, [load])

  const avg = cell.posts ? cell.emv / cell.posts : 0

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to heatmap
      </button>
      <div>
        <h2 className="text-lg font-bold text-gray-900">{categoryLabel}{format ? <span className="text-gray-400 font-normal"> · {format}</span> : null}</h2>
        <p className="text-sm text-gray-500">Tenants with content in this category &amp; format, ranked by EMV. Click a tenant to see its posts.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Total EMV" value={fmtEMV(cell.emv)} icon={DollarSign} colorScheme="emerald" />
        <KPICard label="Posts" value={String(cell.posts)} icon={BarChart3} colorScheme="purple" />
        <KPICard label="Avg EMV / Post" value={fmtEMV(avg)} icon={TrendingUp} colorScheme="amber" />
        <KPICard label="Tenants" value={loading ? '—' : String(tenants.length)} icon={Users} colorScheme="blue" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        {loading ? (
          <div className="flex items-center justify-center min-h-[240px]"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        ) : error ? (
          <div className="min-h-[160px] flex items-center justify-center text-sm text-red-600">{error}</div>
        ) : tenants.length === 0 ? (
          <div className="min-h-[160px] flex items-center justify-center text-sm text-gray-400">No tenants with content for this category &amp; format.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {tenants.map((t) => (
              <div
                key={t.customer_id}
                onClick={() => onTenantClick(t.customer_id, t.customer_name)}
                className="cursor-pointer rounded-xl border border-gray-200 bg-gradient-to-br from-white to-slate-50/40 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-blue-200 transition-all duration-300"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-semibold text-gray-900 truncate" title={t.customer_name}>{t.customer_name}</h3>
                  <span className="inline-flex items-center gap-1 flex-shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600" title={`${Math.round(t.followers).toLocaleString()} followers`}>
                    <Users className="h-2.5 w-2.5" />{fmtNum(t.followers)}
                  </span>
                </div>
                <div className="mt-1 text-2xl font-bold text-emerald-600 leading-none">{fmtEMV(t.total_emv)}</div>
                <div className="mt-1 text-[9px] font-semibold uppercase tracking-wider text-gray-400">Total EMV</div>
                <div className="mt-3 pt-2.5 border-t border-gray-100 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-medium text-gray-600">
                  <span className="flex items-center gap-1"><BarChart3 className="h-3 w-3 text-gray-400" />{t.total_posts} posts</span>
                  <span className="flex items-center gap-1"><Eye className="h-3 w-3 text-purple-500" />{fmtNum(t.total_views)}</span>
                  <span className="flex items-center gap-1"><Heart className="h-3 w-3 text-rose-500" />{fmtNum(t.total_likes)}</span>
                  <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3 text-blue-500" />{fmtNum(t.total_comments)}</span>
                  <span className="flex items-center gap-1"><Share2 className="h-3 w-3 text-green-500" />{fmtNum(t.total_shares)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default TenantCategoryView
